// Webmail and ISP mailbox domains: an artisan using Gmail must not get the Gmail logo.
const CONSUMER_MAIL_DOMAINS = [
  /^gmail\.com$/,
  /^googlemail\.com$/,
  /^hotmail\.[a-z.]+$/,
  /^outlook\.[a-z.]+$/,
  /^live\.[a-z.]+$/,
  /^msn\.com$/,
  /^yahoo\.[a-z.]+$/,
  /^icloud\.com$/,
  /^me\.com$/,
  /^orange\.fr$/,
  /^wanadoo\.fr$/,
  /^free\.fr$/,
  /^sfr\.fr$/,
  /^neuf\.fr$/,
  /^laposte\.net$/,
  /^bbox\.fr$/,
  /^aol\.com$/,
  /^proton\.me$/,
  /^protonmail\.com$/,
];

const isConsumerMailDomain = (domain: string) =>
  CONSUMER_MAIL_DOMAINS.some(pattern => pattern.test(domain));

const domainFromWebsite = (websiteUrl?: string): string | undefined => {
  if (!websiteUrl) return undefined;
  try {
    const url = new URL(/^https?:\/\//i.test(websiteUrl) ? websiteUrl : `https://${websiteUrl}`);
    return url.hostname.toLowerCase().replace(/^www\./, '') || undefined;
  } catch {
    return undefined;
  }
};

const domainFromEmail = (email?: string): string | undefined => {
  const domain = email?.split('@')[1]?.trim().toLowerCase();
  if (!domain || isConsumerMailDomain(domain)) return undefined;
  return domain;
};

/**
 * Domain used to fetch a company logo: the website first, then the company email. Only the
 * domain ever leaves the app, never the full address.
 */
export const getCompanyLogoDomain = ({
  websiteUrl,
  emails,
}: {
  websiteUrl?: string;
  emails?: (string | undefined)[];
}): string | undefined =>
  domainFromWebsite(websiteUrl) ??
  (emails ?? []).map(domainFromEmail).find((domain): domain is string => !!domain);

export const getBrandfetchLogoUrl = (domain: string) =>
  `https://cdn.brandfetch.io/${domain}/fallback/404/icon?c=${import.meta.env.VITE_BRANDFETCH_CLIENT_ID}`;

export const getNameInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

/**
 * Name to show for a contact. `displayName` is computed by the API, but some payloads (public
 * QR export) may not carry it yet: fall back on the raw fields.
 */
export const getContactName = (contact: {
  displayName?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
}): string =>
  contact.displayName ??
  contact.companyName ??
  [contact.firstName, contact.lastName].filter(Boolean).join(' ');
